import React, { useEffect, useMemo, useRef, useState } from "react";
import { useMatkaBetPlacement } from "./useMatkaBetPlacement";
import { useDispatch, useSelector } from "react-redux";
import { openLoginModal, openSignupModal } from "../../../features/slices/ModalSlice";
import { fetchMatkaSuggestions } from "../../../api/services/matka";
import { QUICK_MARKETS } from "./constants";
import MatkaFooter from "./MatkaFooter";
import MatkaSessionSelect, {
  applySessionToBids,
  getSessionOptions,
} from "./MatkaSessionSelect";

function flattenPanaSuggestions(group) {
  if (!group) return [];
  return [...(group.sp || []), ...(group.dp || []), ...(group.tp || [])].map(String);
}

export default function MatkaFullSangam({
  selectedMarket,
  selectedGameType,
  onBackToGameTypes,
  onBackToMarkets,
  onQuickMarketSelect,
  onSelectTab,
  onPlaceBet,
}) {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);

  const [sessionStatus, setSessionStatus] = useState(() =>
    getSessionOptions(selectedGameType?.betDirection)[0].toLowerCase()
  );
  const [openPanna, setOpenPanna] = useState("");
  const [closePana, setClosePana] = useState("");
  const [enterPoints, setEnterPoints] = useState("");
  const [openSuggestions, setOpenSuggestions] = useState([]);
  const [closeSuggestions, setCloseSuggestions] = useState([]);
  const [bids, setBids] = useState([]);
  const [notice, setNotice] = useState("");
  const { openReview, betModals } = useMatkaBetPlacement({
    market: selectedMarket,
    gameType: selectedGameType,
    session: sessionStatus,
    bids,
    onPlaced: () => setBids([]),
  });
  const openRef = useRef(null);
  const closeRef = useRef(null);
  const pointsRef = useRef(null);
  const confirmedOpen = useRef(new Set());
  const confirmedClose = useRef(new Set());

  const totalBids = bids.length;
  const totalPoints = useMemo(
    () => bids.reduce((sum, bid) => sum + (Number(bid.points) || 0), 0),
    [bids]
  );

  useEffect(() => {
    if (!openPanna || openPanna.length >= 3) {
      setOpenSuggestions([]);
      return undefined;
    }

    const digits = openPanna;
    let active = true;
    const timer = setTimeout(() => {
      fetchMatkaSuggestions("full_sangam", digits)
        .then((data) => {
          const list = flattenPanaSuggestions(data.open_pana_suggestions);
          list.forEach((pana) => confirmedOpen.current.add(pana));
          if (active) setOpenSuggestions(list);
        })
        .catch(() => {
          if (active) setOpenSuggestions([]);
        });
    }, 300);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [openPanna]);

  useEffect(() => {
    if (!closePana || closePana.length >= 3) {
      setCloseSuggestions([]);
      return undefined;
    }

    const digits = `-${closePana}`;
    let active = true;
    const timer = setTimeout(() => {
      fetchMatkaSuggestions("full_sangam", digits)
        .then((data) => {
          const list = flattenPanaSuggestions(data.close_pana_suggestions);
          list.forEach((pana) => confirmedClose.current.add(pana));
          if (active) setCloseSuggestions(list);
        })
        .catch(() => {
          if (active) setCloseSuggestions([]);
        });
    }, 300);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [closePana]);

  const handleOpenChange = (event) => {
    const next = event.target.value.replace(/\D/g, "").slice(0, 3);
    setOpenPanna(next);
    if (next.length === 3) {
      setOpenSuggestions([]);
      closeRef.current?.focus();
    }
  };

  const handleCloseChange = (event) => {
    const next = event.target.value.replace(/\D/g, "").slice(0, 3);
    setClosePana(next);
    if (next.length === 3) {
      setCloseSuggestions([]);
      pointsRef.current?.focus();
    }
  };

  const handlePointsChange = (event) => {
    setEnterPoints(event.target.value.replace(/\D/g, "").slice(0, 5));
  };

  const selectOpen = (pana) => {
    confirmedOpen.current.add(pana);
    setOpenPanna(pana);
    setOpenSuggestions([]);
    closeRef.current?.focus();
  };

  const selectClose = (pana) => {
    confirmedClose.current.add(pana);
    setClosePana(pana);
    setCloseSuggestions([]);
    pointsRef.current?.focus();
  };

  const confirmPana = async (side, value) => {
    if (!/^\d{3}$/.test(value)) return false;
    const confirmed = side === "open" ? confirmedOpen.current : confirmedClose.current;
    if (confirmed.has(value)) return true;

    const digits = side === "close" ? `-${value.slice(0, 2)}` : value.slice(0, 2);
    const data = await fetchMatkaSuggestions("full_sangam", digits);
    const list = flattenPanaSuggestions(
      side === "close" ? data.close_pana_suggestions : data.open_pana_suggestions
    );
    list.forEach((pana) => confirmed.add(pana));
    return confirmed.has(value);
  };

  const handleAddBid = async (event) => {
    event?.preventDefault();
    if (!enterPoints || !openPanna || !closePana) {
      setNotice("Enter points, open pana and close pana.");
      return;
    }

    const pointsValue = Number(enterPoints);
    if (!Number.isInteger(pointsValue) || pointsValue < 1 || pointsValue > 10000) {
      setNotice("Points must be between 1 and 10,000.");
      return;
    }

    const openIsValid = await confirmPana("open", openPanna);
    const closeIsValid = await confirmPana("close", closePana);
    if (!openIsValid || !closeIsValid) {
      setNotice("Choose open pana and close pana from the suggestions.");
      return;
    }

    const pair = `${openPanna}-${closePana}`;
    setBids((currentBids) => {
      const existingBid = currentBids.find(
        (bid) => bid.panna === pair && bid.gameType === sessionStatus
      );
      if (existingBid) {
        const updated = {
          ...existingBid,
          points: Number(existingBid.points) + pointsValue,
        };
        return [updated, ...currentBids.filter((bid) => bid.id !== existingBid.id)];
      }

      return [
        {
          id: Date.now(),
          panna: pair,
          gameType: sessionStatus,
          points: pointsValue,
        },
        ...currentBids,
      ];
    });
    setOpenPanna("");
    setClosePana("");
    setEnterPoints("");
    setOpenSuggestions([]);
    setCloseSuggestions([]);
  };

  const handleSessionChange = (nextSession) => {
    setSessionStatus(nextSession);
    setBids((currentBids) =>
      applySessionToBids(currentBids, nextSession, (bid) => bid.panna)
    );
  };

  const handleDeleteBid = (bidId) => {
    setBids((currentBids) => currentBids.filter((bid) => bid.id !== bidId));
  };

  const handleContinue = () => {
    if (!totalBids) return;
    if (!user?.userLoggedIn) {
      dispatch(openLoginModal("login"));
      return;
    }
    openReview();
  };

  return (
    <div className="matka-betting-screen-wrapper">
      {/* Top Header */}
      <header className="matka-betting-header">
        <div className="container-fluid custom-padding d-flex flex-column flex-md-row justify-content-between align-items-center gap-3">
          {/* Brand Logo & ONLINE badge */}
          <div className="d-flex align-items-center gap-2">
            <span
              className="matka-betting-brand-text"
              onClick={onBackToMarkets}
            >
              Matka
            </span>
            <span className="matka-online-badge">ONLINE</span>
          </div>

          {/* Quick Market Switcher */}
          <div className="matka-quick-market-switcher d-flex align-items-center">
            {QUICK_MARKETS.map((qm, idx) => (
              <React.Fragment key={qm.id}>
                <button
                  type="button"
                  className={`matka-quick-market-btn ${
                    selectedMarket?.id === qm.id ? "active" : ""
                  }`}
                  onClick={() => onQuickMarketSelect(qm.id)}
                >
                  {qm.icon && <span className="me-1">{qm.icon}</span>}
                  {qm.name}
                </button>
                {idx < QUICK_MARKETS.length - 1 && (
                  <span className="matka-switcher-dot">•</span>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* User Auth Buttons */}
          <div className="d-flex align-items-center gap-2">
            {!user?.userLoggedIn ? (
              <>
                <button
                  type="button"
                  className="matka-betting-login-btn"
                  onClick={() => dispatch(openLoginModal("login"))}
                >
                  LOGIN
                </button>
                <button
                  type="button"
                  className="matka-betting-signup-btn"
                  onClick={() => dispatch(openSignupModal())}
                >
                  SIGN UP
                </button>
              </>
            ) : (
              <span className="matka-user-logged-badge">
                {user?.user?.name || user?.user?.username || "Player"}
              </span>
            )}
          </div>
        </div>
      </header>

      {/* Main Body */}
      <main className="matka-betting-body pt-3 pb-4">
        <div className="matka-main-centered-container">
          {/* Back to Market Link */}
          <button
            type="button"
            className="matka-back-to-market-link d-inline-flex align-items-center mb-2"
            onClick={onBackToGameTypes}
          >
            <i className="bi bi-chevron-left me-1"></i>
            Back to{" "}
            {selectedMarket?.name
              ? selectedMarket.name.charAt(0) +
                selectedMarket.name.slice(1).toLowerCase()
              : "Mahadevi"}
          </button>

          {/* Screen Title */}
          <h1 className="matka-panna-bulk-title mb-4">Full Sangam</h1>
          <div className="mb-4">
            <label className="matka-panna-bulk-label">GAME TYPE</label>
            <MatkaSessionSelect
              betDirection={selectedGameType?.betDirection}
              value={sessionStatus}
              onChange={handleSessionChange}
              variant="bulk"
            />
          </div>

          {/* Form: Row 1 - OPEN PANNA and CLOSE PANA */}
          <div className="row g-3 mb-3">
            <div className="col-12 col-md-6">
              <label className="matka-panna-bulk-label">OPEN PANNA</label>
              <div className="matka-sangam-field">
                <input
                  ref={openRef}
                  type="text"
                  inputMode="numeric"
                  className="matka-panna-bulk-input"
                  placeholder="379"
                  value={openPanna}
                  onChange={handleOpenChange}
                />
                {openSuggestions.length > 0 && (
                  <div className="matka-sangam-suggestions" role="listbox">
                    {openSuggestions.map((pana) => (
                      <button
                        key={pana}
                        type="button"
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => selectOpen(pana)}
                      >
                        {pana}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="col-12 col-md-6">
              <label className="matka-panna-bulk-label">CLOSE PANA</label>
              <div className="matka-sangam-field">
                <input
                  ref={closeRef}
                  type="text"
                  inputMode="numeric"
                  className="matka-panna-bulk-input"
                  placeholder="245"
                  value={closePana}
                  onChange={handleCloseChange}
                />
                {closeSuggestions.length > 0 && (
                  <div className="matka-sangam-suggestions" role="listbox">
                    {closeSuggestions.map((pana) => (
                      <button
                        key={pana}
                        type="button"
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => selectClose(pana)}
                      >
                        {pana}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Form: Row 2 - ENTER POINTS (Full Width) */}
          <div className="mb-3">
            <label className="matka-panna-bulk-label">ENTER POINTS</label>
            <input
              ref={pointsRef}
              type="text"
              inputMode="numeric"
              className="matka-panna-bulk-input"
              placeholder="Enter Points (e.g. 100)"
              value={enterPoints}
              onChange={handlePointsChange}
            />
          </div>

          {/* Add Bid Button (Right-Aligned) */}
          <div className="d-flex justify-content-end mb-4">
            <button
              type="button"
              className="matka-add-bid-btn"
              onClick={handleAddBid}
            >
              ADD BID
            </button>
          </div>

          {/* Current Bids List Card */}
          <div className="matka-bids-list-card mb-4">
            <h3 className="matka-bids-list-title">Current Bids List</h3>
            <div className="table-responsive">
              <table className="matka-bids-table">
                <thead>
                  <tr>
                    <th className="th-panna">Panna</th>
                    <th className="th-type text-center">Game Type</th>
                    <th className="th-points text-end">Points (₹)</th>
                    <th className="th-action" aria-label="Delete"></th>
                  </tr>
                </thead>
                <tbody>
                  {bids.map((bid) => (
                    <tr key={bid.id}>
                      <td className="td-panna">{bid.panna}</td>
                      <td className="td-type text-center">{bid.gameType}</td>
                      <td className="td-points text-end">{bid.points}</td>
                      <td className="td-action text-end">
                        <button
                          type="button"
                          className="matka-jodi-delete-btn"
                          onClick={() => handleDeleteBid(bid.id)}
                          aria-label={`Delete ${bid.panna}`}
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* In-page Action Bar with Bids, Points, and CONTINUE */}
          <div className="matka-bulk-summary-bar">
            <div className="d-flex align-items-center gap-3">
              {/* Bids Counter */}
              <div className="matka-bulk-counter-pill">
                <span className="matka-bulk-counter-val">{totalBids}</span>
                <span className="matka-bulk-counter-label">Bids</span>
              </div>

              {/* Total Points Counter */}
              <div className="matka-bulk-counter-pill">
                <span className="matka-bulk-counter-val">
                  {totalPoints.toLocaleString()}
                </span>
                <span className="matka-bulk-counter-label">Total Points</span>
              </div>
            </div>

            {/* Continue Action Button */}
            <button
              type="button"
              className="matka-bulk-continue-btn"
              onClick={handleContinue}
              disabled={totalBids === 0}
            >
              CONTINUE
            </button>
          </div>
        </div>
      </main>

      {notice && (
        <div className="matka-sangam-popup" role="dialog" aria-modal="true">
          <div className="matka-sangam-popup-box">
            <p>{notice}</p>
            <button type="button" onClick={() => setNotice("")}>
              OK
            </button>
          </div>
        </div>
      )}

      {betModals}

      {/* Matka Footer */}
      <MatkaFooter
        onSelectTab={onSelectTab}
        onBackToMarkets={onBackToMarkets}
      />
    </div>
  );
}
